
import { useState, useEffect } from "react";
import { RecipeIdea, RecipeCategory, RecipeDifficulty } from "@/components/admin/tools/recipe-ideas/types";
import { db } from "@/integrations/cloudflare/client";
import { useToolUsageTracker } from "@/utils/toolUsageTracker";

interface UseRecipeGeneratorProps {
  instanceId: string;
  postId?: string;
}

export const useRecipeGenerator = ({ instanceId, postId }: UseRecipeGeneratorProps) => {
  const [recipes, setRecipes] = useState<RecipeIdea[]>([]);
  const [filteredRecipes, setFilteredRecipes] = useState<RecipeIdea[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeIdea | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageCount, setUsageCount] = useState(0);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState<RecipeCategory | 'All'>('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState<RecipeDifficulty | 'All'>('All');
  const [kidFriendlyOnly, setKidFriendlyOnly] = useState(false);
  
  const { trackToolUsage } = useToolUsageTracker('recipeGenerator', postId);

  // Get usage count from local storage
  useEffect(() => {
    const localKey = `recipeGenerator_${instanceId}_uses`;
    const localUses = localStorage.getItem(localKey) || '0';
    setUsageCount(parseInt(localUses, 10));
  }, [instanceId]);

  // Fetch recipes
  const fetchRecipes = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // The holiday_recipes table should exist now after running the migration
      const { data, error } = await db
        .from('holiday_recipes')
        .select('*');
        
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        // Transform the data to match our RecipeIdea type
        const transformedData: RecipeIdea[] = data.map(recipe => ({
          id: recipe.id,
          title: recipe.title,
          description: recipe.description,
          ingredients: recipe.ingredients,
          instructions: recipe.instructions,
          prepTime: recipe.prep_time,
          cookTime: recipe.cook_time,
          totalTime: recipe.total_time,
          servings: recipe.servings,
          difficulty: recipe.difficulty as RecipeDifficulty,
          category: recipe.category as RecipeCategory,
          tags: recipe.tags,
          imageUrl: recipe.image_url,
          kidFriendly: recipe.kid_friendly,
          createdAt: recipe.created_at,
          updatedAt: recipe.updated_at
        }));
        
        setRecipes(transformedData);
      } else {
        // For demo purposes, generate mock data if no recipes exist
        generateMockRecipes();
      }
    } catch (err) {
      console.error('Error fetching recipes:', err);
      setError('Failed to load recipes. Please try again later.');
      
      // For demo purposes, generate mock data if database connection fails
      generateMockRecipes();
    } finally {
      setIsLoading(false);
    }
  };
  
  // Initialize with mock data for demo purposes
  const generateMockRecipes = () => {
    const mockRecipes: RecipeIdea[] = [
      {
        id: '1',
        title: 'Gingerbread Cookies',
        description: 'Classic holiday gingerbread cookies perfect for decorating with the kids.',
        ingredients: [
          '3 cups all-purpose flour',
          '1 1/2 tsp baking powder',
          '3/4 tsp baking soda',
          '1/4 tsp salt',
          '1 tbsp ground ginger',
          '1 3/4 tsp ground cinnamon',
          '1/4 tsp ground cloves',
          '6 tbsp unsalted butter',
          '3/4 cup dark brown sugar',
          '1 large egg',
          '1/2 cup molasses',
          '2 tsp vanilla extract'
        ],
        instructions: '1. In a small bowl, whisk together the flour, baking powder, baking soda, salt, ginger, cinnamon, and cloves.\n\n2. In a large bowl using a hand-held mixer or stand mixer, beat the butter and brown sugar together until fluffy. Beat in the egg, molasses, and vanilla extract.\n\n3. Slowly mix in the dry ingredients until combined.\n\n4. Divide the dough into two equal parts. Wrap each in plastic wrap and chill for at least 3 hours.\n\n5. Preheat oven to 350°F (175°C).\n\n6. Roll out the dough on a floured surface to 1/4-inch thickness. Cut into shapes with cookie cutters.\n\n7. Place cookies on parchment-lined baking sheets.\n\n8. Bake for 8-10 minutes or until edges are just starting to brown.\n\n9. Cool on the baking sheet for 5 minutes, then transfer to a wire rack to cool completely.\n\n10. Decorate as desired with icing and sprinkles.',
        prepTime: '30 mins',
        cookTime: '10 mins',
        totalTime: '4 hours (including chilling)',
        servings: 24,
        difficulty: 'Medium',
        category: 'Holiday Cookies',
        tags: ['Christmas', 'Cookies', 'Dessert', 'Gingerbread'],
        kidFriendly: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: '2',
        title: 'Hot Chocolate with Peppermint',
        description: 'Rich and creamy hot chocolate with a festive peppermint twist.',
        ingredients: [
          '4 cups milk',
          '1/4 cup cocoa powder',
          '1/4 cup granulated sugar',
          '1/2 cup semi-sweet chocolate chips',
          '1/2 tsp vanilla extract',
          '1/4 tsp peppermint extract',
          'Whipped cream for topping',
          'Crushed candy canes for garnish'
        ],
        instructions: '1. In a medium saucepan, whisk together the cocoa powder and sugar.\n\n2. Add milk and whisk until combined.\n\n3. Heat over medium heat, stirring occasionally, until the mixture begins to simmer.\n\n4. Add chocolate chips and stir until melted and smooth.\n\n5. Remove from heat and stir in vanilla and peppermint extracts.\n\n6. Pour into mugs and top with whipped cream and crushed candy canes.',
        prepTime: '5 mins',
        cookTime: '10 mins',
        totalTime: '15 mins',
        servings: 4,
        difficulty: 'Easy',
        category: 'Drinks',
        tags: ['Christmas', 'Peppermint', 'Hot Chocolate', 'Beverage'],
        kidFriendly: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: '3',
        title: 'Roast Turkey with Herb Butter',
        description: 'A perfectly juicy and flavorful turkey with herb butter under the skin.',
        ingredients: [
          '1 whole turkey (12-14 lbs), thawed if frozen',
          '1 cup (2 sticks) unsalted butter, softened',
          '2 tbsp fresh rosemary, finely chopped',
          '2 tbsp fresh thyme leaves',
          '2 tbsp fresh sage, finely chopped',
          '4 garlic cloves, minced',
          '2 tsp salt',
          '1 tsp black pepper',
          '1 lemon, zested and quartered',
          '1 onion, quartered',
          '2 carrots, roughly chopped',
          '2 celery stalks, roughly chopped',
          '2 cups chicken broth'
        ],
        instructions: '1. Preheat oven to 325°F (165°C).\n\n2. In a bowl, mix the softened butter with herbs, garlic, lemon zest, salt, and pepper until well combined.\n\n3. Remove the turkey from packaging and remove the neck and giblets from the cavity. Pat the turkey dry with paper towels.\n\n4. Carefully loosen the skin over the breast and thighs. Spread about 2/3 of the herb butter underneath the skin, directly onto the meat.\n\n5. Rub the remaining butter over the outside of the turkey.\n\n6. Stuff the cavity with the quartered lemon and some of the onion, carrots, and celery.\n\n7. Place the remaining vegetables in the bottom of a large roasting pan and place the turkey on top, breast side up.\n\n8. Pour the chicken broth into the bottom of the pan.\n\n9. Roast for about 3-4 hours, or until a meat thermometer inserted into the thickest part of the thigh registers 165°F (74°C).\n\n10. Baste the turkey with the pan juices every 30 minutes after the first hour.\n\n11. If the turkey browns too quickly, cover loosely with aluminum foil.\n\n12. Let the turkey rest for at least 20-30 minutes before carving.',
        prepTime: '30 mins',
        cookTime: '3-4 hours',
        totalTime: '4-5 hours',
        servings: 8,
        difficulty: 'Hard',
        category: 'Christmas Dinner',
        tags: ['Christmas', 'Turkey', 'Main Dish', 'Holiday'],
        kidFriendly: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: '4',
        title: 'Christmas Morning Cinnamon Rolls',
        description: 'Soft, fluffy cinnamon rolls perfect for Christmas morning breakfast.',
        ingredients: [
          '4 cups all-purpose flour',
          '1/3 cup granulated sugar',
          '1 tsp salt',
          '2 1/4 tsp instant yeast',
          '1 egg',
          '1/3 cup unsalted butter, melted',
          '1 cup warm milk',
          'Filling:',
          '1/2 cup unsalted butter, softened',
          '1 cup brown sugar',
          '2 tbsp ground cinnamon',
          'Frosting:',
          '4 oz cream cheese, softened',
          '1/4 cup unsalted butter, softened',
          '1 1/2 cups powdered sugar',
          '1/2 tsp vanilla extract'
        ],
        instructions: '1. In a large bowl, mix flour, sugar, salt, and yeast.\n\n2. Add egg, melted butter, and warm milk. Stir until a soft dough forms.\n\n3. Knead on a floured surface for 5-7 minutes until smooth and elastic.\n\n4. Place in a greased bowl, cover, and let rise in a warm place for 1 hour, or until doubled in size.\n\n5. Roll the dough into a 16x12 inch rectangle on a floured surface.\n\n6. Spread softened butter over the dough, leaving a 1/2-inch border.\n\n7. Mix brown sugar and cinnamon, then sprinkle evenly over the butter.\n\n8. Roll up tightly from the long edge and pinch the seam to seal.\n\n9. Cut into 12 equal rolls and place in a greased 9x13 inch baking pan.\n\n10. Cover and let rise for 30-40 minutes, or until puffy.\n\n11. Preheat oven to 350°F (175°C) and bake for 20-25 minutes until golden brown.\n\n12. For the frosting, beat cream cheese and butter until fluffy. Add powdered sugar and vanilla, beat until smooth.\n\n13. Spread frosting over warm rolls before serving.',
        prepTime: '40 mins',
        cookTime: '25 mins',
        totalTime: '2 hours 30 mins',
        servings: 12,
        difficulty: 'Medium',
        category: 'Breakfast',
        tags: ['Christmas', 'Breakfast', 'Cinnamon Rolls', 'Holiday'],
        kidFriendly: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: '5',
        title: 'Peppermint Bark',
        description: 'Easy and festive peppermint bark with layers of white and dark chocolate.',
        ingredients: [
          '12 oz dark chocolate, chopped',
          '12 oz white chocolate, chopped',
          '1/2 tsp peppermint extract',
          '6 candy canes, crushed'
        ],
        instructions: '1. Line a baking sheet with parchment paper.\n\n2. Melt the dark chocolate in a heatproof bowl over a pot of simmering water, stirring occasionally until smooth.\n\n3. Pour the melted dark chocolate onto the prepared baking sheet and spread into an even layer about 1/4 inch thick. Let cool until set but still slightly soft, about 15 minutes.\n\n4. Meanwhile, melt the white chocolate in the same way.\n\n5. Stir the peppermint extract into the melted white chocolate.\n\n6. Pour the white chocolate over the dark chocolate layer and spread evenly.\n\n7. Immediately sprinkle the crushed candy canes over the white chocolate layer.\n\n8. Let cool completely until hardened, about 1 hour at room temperature or 30 minutes in the refrigerator.\n\n9. Break into pieces and store in an airtight container.',
        prepTime: '15 mins',
        cookTime: '5 mins',
        totalTime: '1 hour 20 mins',
        servings: 12,
        difficulty: 'Easy',
        category: 'Holiday Treats',
        tags: ['Christmas', 'Candy', 'Peppermint', 'No-Bake'],
        kidFriendly: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ];
    
    setRecipes(mockRecipes);
  };
  
  // Apply filters to recipes
  useEffect(() => {
    if (recipes.length === 0) {
      fetchRecipes();
      return;
    }
    
    let filtered = [...recipes];
    
    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(recipe => recipe.category === selectedCategory);
    }
    
    // Apply difficulty filter
    if (selectedDifficulty !== 'All') {
      filtered = filtered.filter(recipe => recipe.difficulty === selectedDifficulty);
    }
    
    // Apply kid-friendly filter
    if (kidFriendlyOnly) {
      filtered = filtered.filter(recipe => recipe.kidFriendly);
    }
    
    setFilteredRecipes(filtered);
  }, [recipes, selectedCategory, selectedDifficulty, kidFriendlyOnly]);
  
  // Generate recipe ideas with tracking
  const generateIdeas = () => {
    // Track usage
    trackToolUsage({
      action: 'generate',
      metadata: {
        category: selectedCategory,
        difficulty: selectedDifficulty,
        kidFriendlyOnly
      }
    });
    
    // Reset selected recipe
    setSelectedRecipe(null);
    
    // Shuffle recipes for a "new" set
    const shuffled = [...filteredRecipes].sort(() => Math.random() - 0.5);
    setFilteredRecipes(shuffled);
    
    // Increment usage count in local storage
    const localKey = `recipeGenerator_${instanceId}_uses`;
    const newCount = usageCount + 1;
    localStorage.setItem(localKey, newCount.toString());
    setUsageCount(newCount);
  };
  
  // Reset filters
  const resetFilters = () => {
    setSelectedCategory('All');
    setSelectedDifficulty('All');
    setKidFriendlyOnly(false);
    setSelectedRecipe(null);
    
    // Track usage
    trackToolUsage({
      action: 'reset'
    });
  };
  
  return {
    recipes: filteredRecipes,
    selectedRecipe,
    setSelectedRecipe,
    isLoading,
    error,
    usageCount,
    selectedCategory,
    setSelectedCategory,
    selectedDifficulty,
    setSelectedDifficulty,
    kidFriendlyOnly,
    setKidFriendlyOnly,
    generateIdeas,
    resetFilters
  };
};
